import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, LocationProvider, EventsProvider, WalletProvider, FeatureFlagsProvider, useFeatureFlags } from '@contexts'
import { ToastProvider } from '@contexts/ToastContext'
import { Layout } from '@shared'
import { EmailVerificationHandler } from '@components'
import { 
  Home, 
  Event, 
  Login, 
  Register, 
  CompleteRegistration,
  Checkout,
  GoogleCallback,
  LocationSelection,
  Maintenance,
  EmailVerificationPage
} from '@pages'
import { useServiceWorkerUpdate } from './hooks/useServiceWorkerUpdate'

// Lazy load secondary routes for Code Splitting
const Wallet = lazy(() => import('./pages/Wallet'))
const Profile = lazy(() => import('./pages/Profile'))
const TermsOfService = lazy(() => import('./pages/TermsOfService/TermsOfService'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'))
const RefundPolicy = lazy(() => import('./pages/RefundPolicy/RefundPolicy'))
const About = lazy(() => import('./pages/About/About'))
const TransactionError = lazy(() => import('./pages/TransactionError/TransactionError'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 min stale time
      gcTime: 5 * 60 * 1000, // 5 min gc time
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <FeatureFlagsProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </FeatureFlagsProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

function AppContent() {
  useServiceWorkerUpdate();
  const { pathname } = useLocation();
  const flags = useFeatureFlags();

  // Se for um arquivo estático, não renderiza a aplicação
  const isStaticFile = /\.(png|jpg|jpeg|gif|svg|ico|webp|js|css|md|json)$/i.test(pathname);
  if (isStaticFile) return null;

  // Se estiver em modo de manutenção, exibe a tela de manutenção
  if (flags.ENABLE_MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  return (
    <AuthProvider>
      <EmailVerificationHandler />
      <WalletProvider>
        <LocationProvider>
          <Suspense fallback={<div className="min-h-screen bg-black flex items-center justify-center text-white">Carregando...</div>}>
            <Routes>
              <Route path="/" element={<LocationSelection />} />
              <Route path="/validar-email" element={<EmailVerificationPage />} />
              <Route path="/:citySlug" element={
                <EventsProvider>
                  <Layout />
                </EventsProvider>
              }>
                {/* Páginas públicas */}
                <Route index element={<Home />} />
                <Route path="evento/:id" element={<Event />} />
                <Route path="termos-de-uso" element={<TermsOfService />} />
                <Route path="politica-de-privacidade" element={<PrivacyPolicy />} />
                <Route path="politica-de-reembolso" element={<RefundPolicy />} />
                <Route path="sobre" element={<About />} />

                {/* Autenticação */}
                <Route path="login" element={<Login />} />
                <Route path="cadastro" element={<Register />} />
                <Route path="auth/google/callback" element={<GoogleCallback />} />
                <Route path="completar-cadastro" element={<CompleteRegistration />} />

                {/* Área do usuário */}
                <Route path="checkout/:eventId" element={<Checkout />} />
                <Route path="transacao-erro" element={<TransactionError />} />
                <Route path="carteira" element={<Wallet />} />
                <Route path="perfil" element={<Profile />} />
              </Route>
            </Routes>
          </Suspense>
        </LocationProvider>
      </WalletProvider>
    </AuthProvider>
  );
}

export default App
